import { Injectable, OnDestroy } from '@angular/core';
import { DateService } from '../../shared/services/date.service';
import { FileHandlerBaseService } from '../../shared/services/file-handler/file-handler-base.service';
import { OrderShareService } from './order-share.service';
import { PlatformType } from '@panamax/app-state/lib/models/platform.model';
import { PlatformEnum } from '@panamax/app-state';
import { Subscription } from 'rxjs';
import { Product } from '@shared/models/product.model';
import { Store } from '@ngrx/store';
import { OrderDownloadService } from '@shared/services/file-download/order-download.service';
import { productWithAlternativesSelector } from '@shared/selectors/product.selectors';
import { DownloadFileTypes } from '@shared/constants/order-download-file';
import { Order } from '@usf/ngrx-order';
import { AlertAnalyticsService } from '@usf/ngrx-alerts';

@Injectable({
  providedIn: 'root',
})
export class OrderShareViaNativeService
  extends OrderShareService
  implements OnDestroy
{
  private static readonly CSV_BLOB_TYPE = 'text/csv';
  private productsSubscription: Subscription;
  private productsMap: Map<number, Product>;
  constructor(
    private readonly store: Store,
    private readonly orderDownloadService: OrderDownloadService,
    private readonly fileHandlerService: FileHandlerBaseService,
    private readonly alertAnalyticsService: AlertAnalyticsService,
  ) {
    super();
    this.productsSubscription = this.store
      .select<Map<number, Product>>(productWithAlternativesSelector)
      .subscribe((productsMap: Map<number, Product>) => {
        this.productsMap = productsMap;
      });
  }

  isSupported(platformType: PlatformType): boolean {
    return platformType !== PlatformEnum.desktop;
  }

  async shareCart(order: Order) {
    const currentDate = this.formatDate(new Date());
    const fileName = `ReviewOrderDetail_${order.orderHeader.customerNumber}_${currentDate}`;

    // alerts analyitics
    this.alertAnalyticsService.trackShareFileReviewOrder();

    const payload = this.orderDownloadService.handleCSVDownload([order], {
      fileName,
      fileType: DownloadFileTypes.CSV,
      products: this.productsMap,
      isCart: true,
    });

    await this.fileHandlerService.shareFileNative(
      new Blob([payload], { type: OrderShareViaNativeService.CSV_BLOB_TYPE }),
      fileName,
      '.csv',
      {
        title: fileName,
        dialogTitle: fileName,
      },
    );
  }

  async shareOrderConfirmation(order: Order[]) {
    this.shareSubmittedOrder(order);
  }

  async shareSubmittedOrder(order: Order[]) {
    //analytics
    this.alertAnalyticsService.trackShareFileOrderConfirmation();

    const customerNumber = order[0]?.orderHeader?.customerNumber;
    const currentDate = this.formatDate(new Date());

    const fileName = `Submitted_Order${order[0]?.orderHeader?.tandemOrderNumber}_Cust${customerNumber}_${currentDate}`;

    const payload = this.orderDownloadService.handleCSVDownload(order, {
      fileName,
      fileType: DownloadFileTypes.CSV,
      products: this.productsMap,
    });

    this.shareFile(
      new Blob([payload], { type: OrderShareViaNativeService.CSV_BLOB_TYPE }),
      fileName,
      OrderShareService.CSV_EXTENSION,
    );
  }

  private async shareFile(
    rawData: Blob,
    fileName: string,
    fileExtension: string,
  ) {
    await this.fileHandlerService.shareFileNative(
      rawData,
      fileName,
      fileExtension,
      {
        title: fileName,
        dialogTitle: fileName,
      },
    );
  }

  ngOnDestroy(): void {
    this.productsSubscription?.unsubscribe();
  }
}
